import React, {useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  adminCitiesCache,
  LocationTagsCache,
  recommendationRebuildCache,
} from '../../../common/constants/queryCacheKey';
import {
  AdminCity,
  fetchAdminCities,
  updateCity,
  UpdateCityInput,
} from '../../../common/services/cities';
import {
  getSasUrl,
  uploadToBlob,
} from '../../../common/services/azureBlobUploader';
import {RebuildStatusBanner} from '../../admin/RebuildStatusBanner';
import {
  errorStyle,
  inputStyle,
  labelStyle,
  messageStyle,
  noticeStyle,
  panelStyle,
  primaryButtonStyle,
  screenStyle,
  secondaryButtonStyle,
  titleStyle,
} from '../../admin/adminStyles';

interface CityForm {
  name: string;
  county: string;
  region: string;
  countryCode: string;
  timeZoneId: string;
  cityRangeKm: string;
  latitude: string;
  longitude: string;
  photoUrl: string;
}

const toForm = (city: AdminCity): CityForm => ({
  name: city.name,
  county: city.county,
  region: city.region ?? '',
  countryCode: city.countryCode,
  timeZoneId: city.timeZoneId,
  cityRangeKm: String(city.cityRangeKm),
  latitude: String(city.latitude),
  longitude: String(city.longitude),
  photoUrl: city.photoUrl ?? '',
});

// Only what changed is sent, so a rename or a county change is never sent by accident.
const changes = (city: AdminCity, form: CityForm): UpdateCityInput => {
  const input: UpdateCityInput = {};
  const name = form.name.trim();
  const county = form.county.trim();
  const countryCode = form.countryCode.trim().toUpperCase();
  const timeZoneId = form.timeZoneId.trim();
  const region = form.region.trim();
  const photoUrl = form.photoUrl.trim();
  const range = Number(form.cityRangeKm);
  const latitude = Number(form.latitude);
  const longitude = Number(form.longitude);

  if (name !== city.name) input.name = name;
  if (county !== city.county) input.county = county;
  if (region !== (city.region ?? '')) input.region = region;
  if (countryCode !== city.countryCode) input.countryCode = countryCode;
  if (timeZoneId !== city.timeZoneId) input.timeZoneId = timeZoneId;
  if (range !== city.cityRangeKm) input.cityRangeKm = range;
  if (latitude !== city.latitude) input.latitude = latitude;
  if (longitude !== city.longitude) input.longitude = longitude;
  if (photoUrl !== (city.photoUrl ?? '')) input.photoUrl = photoUrl;

  return input;
};

const keyChangeWarning = (city: AdminCity, input: UpdateCityInput) => {
  const parts: string[] = [];
  if (input.name !== undefined) {
    parts.push(
      `Rename "${city.name}" to "${input.name}"?\n\nPlaces and events are matched to a city by this name (Google's city name, without diacritics). The addresses of this city's ${city.placeCount} places and ${city.eventCount} events and their recommendation rows move to the new name.\n\nThe places importer still sends Google's name: update its city mapping too, or newly imported places will not reach this city. For a display name with diacritics, use Region instead.`,
    );
  }
  if (input.county !== undefined) {
    parts.push(
      `Change the county code from "${city.county}" to "${input.county}"?\n\nImported places and events without a city are assigned by their county, as Google writes it.`,
    );
  }
  if (parts.length > 0) {
    parts.push('A full recommendation rebuild starts after saving.');
  }
  return parts.join('\n\n');
};

const CityCard: React.FC<{city: AdminCity; onEdit: () => void}> = ({
  city,
  onEdit,
}) => (
  <div style={cardStyle}>
    {city.photoUrl ? (
      <img src={city.photoUrl} alt="" style={cardPhotoStyle} />
    ) : (
      <div style={{...cardPhotoStyle, ...noPhotoStyle}}>No photo</div>
    )}
    <div
      style={{
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: 1,
      }}>
      <div style={{color: '#fff', fontWeight: 'bold', fontSize: '1.05rem'}}>
        {city.name}
        <span
          style={{
            color: '#666',
            fontWeight: 'normal',
            fontSize: '0.8rem',
            marginLeft: '8px',
          }}>
          #{city.code}
        </span>
      </div>
      <div style={{color: '#999', fontSize: '0.8rem'}}>
        {[city.region, city.county, city.countryCode]
          .filter(Boolean)
          .join(' · ')}
      </div>
      <div style={{color: '#999', fontSize: '0.8rem'}}>
        {city.placeCount} places · {city.eventCount} events · {city.userCount}{' '}
        users
      </div>
      <button
        onClick={onEdit}
        style={{
          ...secondaryButtonStyle,
          marginTop: '8px',
          alignSelf: 'flex-start',
        }}>
        Edit
      </button>
    </div>
  </div>
);

const CityEditor: React.FC<{
  city: AdminCity;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({city, onClose, onSaved}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CityForm>(() => toForm(city));
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const set =
    (field: keyof CityForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(current => ({...current, [field]: e.target.value}));

  const saveMutation = useMutation({
    mutationFn: (input: UpdateCityInput) => updateCity(city.code, input),
    onSuccess: result => {
      queryClient.invalidateQueries({queryKey: [adminCitiesCache]});
      queryClient.invalidateQueries({queryKey: [LocationTagsCache]});
      if (result.rebuildStarted) {
        queryClient.invalidateQueries({queryKey: [recommendationRebuildCache]});
      }
      onSaved(
        result.rebuildStarted
          ? `${result.location.name} was saved. ${result.addressesRenamed} addresses moved to the new name; the recommendation rebuild has started.`
          : `${result.location.name} was saved.`,
      );
    },
    onError: (e: Error) => setError(e.message),
  });

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);
    setIsUploading(true);
    try {
      const sasUrl = await getSasUrl();
      if (!sasUrl) throw new Error('Could not get an upload link.');
      const url = await uploadToBlob({sasUrl, file, quality: 0.9});
      setForm(current => ({...current, photoUrl: url}));
    } catch (uploadError) {
      setError(
        `The photo could not be uploaded: ${
          uploadError instanceof Error ? uploadError.message : uploadError
        }`,
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = changes(city, form);
    if (Object.keys(input).length === 0) {
      onClose();
      return;
    }
    const warning = keyChangeWarning(city, input);
    if (warning && !window.confirm(warning)) return;
    setError(null);
    saveMutation.mutate(input);
  };

  return (
    <form onSubmit={handleSubmit} style={{...panelStyle, marginBottom: '24px'}}>
      <h2 style={{color: '#fff', margin: '0 0 16px', fontSize: '1.2rem'}}>
        Edit {city.name}
      </h2>

      <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
        <div
          style={{
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}>
          {form.photoUrl ? (
            <img src={form.photoUrl} alt="" style={editorPhotoStyle} />
          ) : (
            <div style={{...editorPhotoStyle, ...noPhotoStyle}}>No photo</div>
          )}
          <label style={{...secondaryButtonStyle, textAlign: 'center'}}>
            {isUploading ? 'Uploading...' : 'Upload a photo'}
            <input
              type="file"
              accept="image/*"
              onChange={handlePhoto}
              disabled={isUploading}
              style={{display: 'none'}}
            />
          </label>
          <label style={labelStyle}>
            Photo URL
            <input
              value={form.photoUrl}
              onChange={set('photoUrl')}
              placeholder="https://..."
              style={inputStyle}
            />
          </label>
          {form.photoUrl && (
            <button
              type="button"
              onClick={() => setForm(current => ({...current, photoUrl: ''}))}
              style={secondaryButtonStyle}>
              Remove photo
            </button>
          )}
        </div>

        <div style={gridStyle}>
          <label style={labelStyle}>
            Name (Google's city name)
            <input
              required
              maxLength={50}
              value={form.name}
              onChange={set('name')}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Region (display name, with diacritics)
            <input
              maxLength={80}
              value={form.region}
              onChange={set('region')}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            County code
            <input
              required
              maxLength={50}
              value={form.county}
              onChange={set('county')}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Country (ISO code)
            <input
              required
              minLength={2}
              maxLength={2}
              value={form.countryCode}
              onChange={set('countryCode')}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Time zone (IANA)
            <input
              required
              value={form.timeZoneId}
              onChange={set('timeZoneId')}
              placeholder="Europe/Bucharest"
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            City range (km)
            <input
              required
              type="number"
              min={1}
              max={500}
              value={form.cityRangeKm}
              onChange={set('cityRangeKm')}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Center latitude
            <input
              required
              type="number"
              step="any"
              min={-90}
              max={90}
              value={form.latitude}
              onChange={set('latitude')}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Center longitude
            <input
              required
              type="number"
              step="any"
              min={-180}
              max={180}
              value={form.longitude}
              onChange={set('longitude')}
              style={inputStyle}
            />
          </label>
        </div>
      </div>

      {error && <p style={{...errorStyle, marginTop: '16px'}}>{error}</p>}

      <div style={{display: 'flex', gap: '12px', marginTop: '20px'}}>
        <button
          type="submit"
          disabled={saveMutation.isPending || isUploading}
          style={primaryButtonStyle}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onClose} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export const CitiesScreen: React.FC = () => {
  const [editingCode, setEditingCode] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const citiesQuery = useQuery({
    queryKey: [adminCitiesCache],
    queryFn: fetchAdminCities,
  });

  const cities = citiesQuery.data ?? [];
  const editing = cities.find(city => city.code === editingCode);
  const keyword = filter.trim().toLowerCase();
  const shown = cities.filter(
    city =>
      !keyword ||
      city.name.toLowerCase().includes(keyword) ||
      (city.region ?? '').toLowerCase().includes(keyword),
  );

  return (
    <div style={screenStyle}>
      <div style={{maxWidth: '1100px', margin: '0 auto'}}>
        <h1 style={titleStyle}>Cities</h1>

        <RebuildStatusBanner />

        {notice && <p style={noticeStyle}>{notice}</p>}

        {editing && (
          <CityEditor
            key={editing.code}
            city={editing}
            onClose={() => setEditingCode(null)}
            onSaved={message => {
              setNotice(message);
              setEditingCode(null);
            }}
          />
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
          }}>
          <input
            placeholder="Search cities..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{...inputStyle, flex: 1}}
          />
          <span style={{color: '#999', flexShrink: 0}}>
            {citiesQuery.isPending
              ? 'Loading...'
              : `${shown.length} of ${cities.length} cities`}
          </span>
        </div>

        <div style={cardGridStyle}>
          {shown.map(city => (
            <CityCard
              key={city.code}
              city={city}
              onEdit={() => {
                setNotice(null);
                setEditingCode(city.code);
                window.scrollTo({top: 0, behavior: 'smooth'});
              }}
            />
          ))}
        </div>

        {citiesQuery.isError && (
          <p style={messageStyle}>Could not load cities. Try again.</p>
        )}
        {citiesQuery.isSuccess && shown.length === 0 && (
          <p style={messageStyle}>
            {cities.length === 0
              ? 'There are no cities yet.'
              : 'No cities match your search.'}
          </p>
        )}
      </div>
    </div>
  );
};

const cardGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: '16px',
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: '#242424',
  border: '1px solid #333',
};

const cardPhotoStyle: React.CSSProperties = {
  width: '100%',
  height: '140px',
  objectFit: 'cover',
  display: 'block',
};

const editorPhotoStyle: React.CSSProperties = {
  width: '100%',
  height: '180px',
  objectFit: 'cover',
  borderRadius: '8px',
  display: 'block',
};

const noPhotoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#333',
  color: '#777',
  fontSize: '0.85rem',
};

const gridStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '300px',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '14px',
  alignContent: 'start',
};
